import type { ReactNode } from 'react'

import { Paper } from '@gto/ui/paper'

type ToasterFrameProps = {
  /** The close control. Always offered — a toaster the user cannot get rid of is a defect. */
  closeControl?: ReactNode
  /** One `ToasterCard`, or the slider that pages between several of them. */
  children: ReactNode
  /** The CTA row, when the announcement has at least one action. */
  actions?: ReactNode
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

/**
 * The floating panel a toaster is served in: the surface, the close control and
 * the CTA row.
 *
 * Separate from `ToasterCard` because in the app one frame holds a SLIDER over
 * several toasters — the close button and the buttons belong to the frame, the
 * chips and copy to each card. The preview reuses the frame around a single card.
 *
 * Knows NOTHING about how a card lays its media out. It once took the active
 * card's `mediaLayout` and dropped its padding for a full-bleed one, which broke
 * as soon as two toasters with different layouts shared it: `FadeSlider` keeps
 * the outgoing slide on screen for 800 ms, so the card sliding out spent that
 * whole transition wearing the incoming card's padding. Each card now brings its
 * own inset, and this surface only owns what is genuinely shared — the bottom
 * rail and the CTA row.
 *
 * A full-bleed card reaches the edges because nothing here pads it, never
 * because it escapes the padding with a negative margin: in the app the card
 * renders inside a `FadeSlider` slide, and a child wider than its slide corrupts
 * the slider's width arithmetic. `overflow-hidden` is what keeps such a card's
 * media on the surface's own corner radius.
 */
export const ToasterFrame = ({
  closeControl,
  children,
  actions,
  onMouseEnter,
  onMouseLeave,
}: ToasterFrameProps) => (
  <Paper
    className="relative isolate z-1 overflow-hidden rounded-md pb-[22px] shadow-8"
    elevation="4"
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {closeControl}
    {children}
    {actions !== undefined && <div className="mt-4 flex gap-4 px-4">{actions}</div>}
  </Paper>
)
